import { ApiService, type ApiRequestConfig } from "@/services/api.service";
import type { components } from "@/types/api.generated";

type OAuthAppRegistrationLevel = "instance" | "workspace";
type OAuthClientType = "confidential" | "public";

export interface OAuthAppRedirectURI {
  id: string;
  uri: string;
  created_at: string;
}

export interface OAuthApp {
  id: string;
  client_id: string;
  name: string;
  description: string;
  homepage_url: string;
  logo_url: string;
  registration_level: OAuthAppRegistrationLevel;
  workspace: string | null;
  workspace_slug: string | null;
  client_type: OAuthClientType;
  allowed_scopes: string[];
  default_scopes: string[];
  is_enabled: boolean;
  is_auto_trusted: boolean;
  require_consent: boolean;
  audience: string;
  has_client_secret: boolean;
  redirect_uris: OAuthAppRedirectURI[];
  created_at: string;
  updated_at: string;
}

export interface OAuthAppCreatePayload {
  name: string;
  description?: string;
  homepage_url?: string;
  logo_url?: string;
  client_type: OAuthClientType;
  redirect_uris: string[];
  allowed_scopes: string[];
  default_scopes?: string[];
  is_auto_trusted?: boolean;
}

export interface OAuthAppWithSecret extends OAuthApp {
  client_secret?: string;
}

export interface OAuthConsent {
  id: string;
  app: string;
  app_name: string;
  workspace: string;
  workspace_slug: string;
  workspace_name: string;
  scopes: string[];
  granted_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
  is_expired: boolean;
}

interface ConsentContextScope {
  name: string;
  description: string;
  is_mandatory: boolean;
}

interface ConsentContextWorkspace {
  id: string;
  slug: string;
  name: string;
  /** Requested scopes this workspace's admin ceiling permits. */
  allowed_scopes: string[];
}

export interface ConsentContext {
  app: {
    client_id: string;
    name: string;
    description: string;
    homepage_url: string;
    logo_url: string;
    registration_level: OAuthAppRegistrationLevel;
    registering_workspace_slug: string | null;
  };
  scopes: ConsentContextScope[];
  workspaces: ConsentContextWorkspace[];
}

/**
 * Derived from the committed contract, not mirrored by hand: the scope-catalog
 * endpoint declares ``category`` as a ChoiceField over ``ScopeCategory``
 * (``cognota/oauth_idp/scopes.py``), so a category added there and missed in
 * ``scope-picker``'s label map is a type error rather than a raw id rendered as
 * a heading — which is how the qualifications group read "SKILLS" (NG-1668).
 */
/**
 * The verified payload behind the `/oauth/unavailable?ctx=<signed>` handoff
 * page. The backend (`cognota/oauth_idp/views_unavailable.py`) checks the
 * signature and returns only the fields the page renders.
 */
export interface UnavailableContext {
  reason: string | null;
  app_name: string | null;
  registering_workspace_name: string | null;
  redirect_uri: string | null;
  state: string | null;
}

export type ScopeCategory = components["schemas"]["OAuthScopeCategoryEnum"];

export interface Scope {
  name: string;
  description: string;
  category: ScopeCategory;
}

export class OAuthIdpService extends ApiService {
  // ----- Scope catalog (public, read-only) -----

  listScopes(config?: ApiRequestConfig): Promise<Scope[]> {
    return this.get<Scope[]>("/api/oauth/scopes/", config);
  }

  // ----- Consent screen -----

  fetchConsentContext(clientId: string, scope: string): Promise<ConsentContext> {
    const params = new URLSearchParams({ client_id: clientId, scope });
    return this.get<ConsentContext>(`/auth/o/consent/context?${params.toString()}`);
  }

  // ----- Workspace-unavailable handoff -----

  /**
   * Verify and decode the signed `ctx` the authorize handler put on the
   * unavailable page's URL. A tampered or expired `ctx` is a 400, which
   * rejects like any other non-2xx.
   */
  fetchUnavailableContext(ctx: string): Promise<UnavailableContext> {
    const params = new URLSearchParams({ ctx });
    return this.get<UnavailableContext>(`/auth/o/unavailable/context?${params.toString()}`);
  }

  // ----- Workspace-owned apps -----

  listWorkspaceApps(workspaceSlug: string, config?: ApiRequestConfig): Promise<OAuthApp[]> {
    return this.get<OAuthApp[]>(`/api/workspaces/${workspaceSlug}/oauth/apps/`, config);
  }

  createWorkspaceApp(
    workspaceSlug: string,
    payload: OAuthAppCreatePayload,
  ): Promise<OAuthAppWithSecret> {
    return this.post<OAuthAppWithSecret>(`/api/workspaces/${workspaceSlug}/oauth/apps/`, payload);
  }

  getWorkspaceApp(
    workspaceSlug: string,
    appId: string,
    config?: ApiRequestConfig,
  ): Promise<OAuthApp> {
    return this.get<OAuthApp>(`/api/workspaces/${workspaceSlug}/oauth/apps/${appId}/`, config);
  }

  updateWorkspaceApp(
    workspaceSlug: string,
    appId: string,
    patch: Partial<OAuthApp>,
  ): Promise<OAuthApp> {
    return this.patch<OAuthApp>(`/api/workspaces/${workspaceSlug}/oauth/apps/${appId}/`, patch);
  }

  deleteWorkspaceApp(workspaceSlug: string, appId: string): Promise<void> {
    return this.delete<void>(`/api/workspaces/${workspaceSlug}/oauth/apps/${appId}/`);
  }

  rotateWorkspaceAppSecret(
    workspaceSlug: string,
    appId: string,
  ): Promise<{ client_secret: string }> {
    return this.post<{ client_secret: string }>(
      `/api/workspaces/${workspaceSlug}/oauth/apps/${appId}/rotate-secret/`,
    );
  }

  addWorkspaceAppRedirectURI(
    workspaceSlug: string,
    appId: string,
    uri: string,
  ): Promise<OAuthAppRedirectURI> {
    return this.post<OAuthAppRedirectURI>(
      `/api/workspaces/${workspaceSlug}/oauth/apps/${appId}/redirect-uris/`,
      { uri },
    );
  }

  removeWorkspaceAppRedirectURI(
    workspaceSlug: string,
    appId: string,
    uriId: string,
  ): Promise<void> {
    return this.delete<void>(
      `/api/workspaces/${workspaceSlug}/oauth/apps/${appId}/redirect-uris/${uriId}/`,
    );
  }

  // ----- Instance-owned apps -----

  listInstanceApps(config?: ApiRequestConfig): Promise<OAuthApp[]> {
    return this.get<OAuthApp[]>("/api/instances/oauth/apps/", config);
  }

  createInstanceApp(payload: OAuthAppCreatePayload): Promise<OAuthAppWithSecret> {
    return this.post<OAuthAppWithSecret>("/api/instances/oauth/apps/", payload);
  }

  getInstanceApp(appId: string, config?: ApiRequestConfig): Promise<OAuthApp> {
    return this.get<OAuthApp>(`/api/instances/oauth/apps/${appId}/`, config);
  }

  updateInstanceApp(appId: string, patch: Partial<OAuthApp>): Promise<OAuthApp> {
    return this.patch<OAuthApp>(`/api/instances/oauth/apps/${appId}/`, patch);
  }

  deleteInstanceApp(appId: string): Promise<void> {
    return this.delete<void>(`/api/instances/oauth/apps/${appId}/`);
  }

  rotateInstanceAppSecret(appId: string): Promise<{ client_secret: string }> {
    return this.post<{ client_secret: string }>(
      `/api/instances/oauth/apps/${appId}/rotate-secret/`,
    );
  }

  addInstanceAppRedirectURI(appId: string, uri: string): Promise<OAuthAppRedirectURI> {
    return this.post<OAuthAppRedirectURI>(`/api/instances/oauth/apps/${appId}/redirect-uris/`, {
      uri,
    });
  }

  removeInstanceAppRedirectURI(appId: string, uriId: string): Promise<void> {
    return this.delete<void>(`/api/instances/oauth/apps/${appId}/redirect-uris/${uriId}/`);
  }

  // ----- User consents -----

  listMyConsents(config?: ApiRequestConfig): Promise<OAuthConsent[]> {
    return this.get<OAuthConsent[]>("/api/users/me/oauth/consents/", config);
  }

  revokeConsent(consentId: string): Promise<void> {
    return this.delete<void>(`/api/users/me/oauth/consents/${consentId}/`);
  }

  // ----- Workspace MCP access -----

  getMcpAccess(workspaceSlug: string, config?: ApiRequestConfig): Promise<McpAccess> {
    return this.get<McpAccess>(`/api/workspaces/${workspaceSlug}/mcp/access/`, config);
  }

  setMcpAccess(workspaceSlug: string, payload: McpAccessUpdatePayload): Promise<McpAccess> {
    return this.put<McpAccess>(`/api/workspaces/${workspaceSlug}/mcp/access/`, payload);
  }

  disableMcpAccess(workspaceSlug: string): Promise<void> {
    return this.delete<void>(`/api/workspaces/${workspaceSlug}/mcp/access/`);
  }

  listMcpClients(workspaceSlug: string, config?: ApiRequestConfig): Promise<McpClient[]> {
    return this.get<McpClient[]>(`/api/workspaces/${workspaceSlug}/mcp/clients/`, config);
  }

  revokeMcpClient(workspaceSlug: string, consentId: string): Promise<void> {
    return this.delete<void>(`/api/workspaces/${workspaceSlug}/mcp/clients/${consentId}/`);
  }
}

export interface McpAccess {
  enabled: boolean;
  allowed_scopes: string[];
  enabled_at: string | null;
  /** The platform-wide MCP scope ceiling — what the workspace can pick from. */
  platform_scopes: string[];
}

export interface McpAccessUpdatePayload {
  allowed_scopes: string[];
}

export interface McpClient {
  consent_id: string;
  client: {
    id: string;
    client_id: string;
    name: string;
    logo_url: string;
  };
  user: {
    id: string;
    email: string;
    display_name: string;
  };
  scopes: string[];
  granted_at: string | null;
  last_used_at: string | null;
  is_expired: boolean;
}
